import { ReactNode, FC, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth, UserRole } from "./components/AuthContext";
import usePageTracking from "./hooks/usePageTracking";

// --- Importação dos Componentes de Página ---
import MarketingHome from "./components/MarketingHome";
import ConsteladorPage from "./components/ConsteladorPage";
import LoginForm from "./components/LoginForm";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import AuthActionHandler from "./components/AuthActionHandler";
import Plans from "./components/Plans";
import Schedule from "./components/Schedule";
import CompleteProfile from "./components/CompleteProfile";
import SettingsPage from "./components/SettingsPage";
import PaymentStatusPage from "./components/Checkout/PaymentStatusPage";
import DashboardPage from "./components/DashboardPage";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import { Therapists } from "./components/Therapists";
import TherapistShow from "./components/TherapistShow";
import OpenRooms from "./components/OpenRooms";
import Meeting from "./components/Meeting";
import CheckoutPage from "./components/Checkout/CheckoutPage";
import Contact, { SobreNosPage } from "./components/Contact";
import TutorialsPage from "./components/TutorialsPage";
import InfluencerRedirect from "./components/InfluencerRedirect";
import EnvironmentBadge from "./components/EnvironmentBadge";
import Footer from "./components/Footer";
import DomainRedirect from "./components/DomainRedirect";
import LogoutRedirect from "./components/LogoutRedirect";
import ClearAnonymousGuestOutsideRoom from "./components/ClearAnonymousGuestOutsideRoom";
import { isAppHost, isMarketingDomainLive, shouldEnforceDomainSplit } from "./constants/siteDomains";
import { MARKETING_HOME_URL } from "./constants/externalLinks";

const MarketingHomeRoute: FC = () => {
  const redirectToMarketing = shouldEnforceDomainSplit() && isAppHost() && isMarketingDomainLive();

  useEffect(() => {
    if (redirectToMarketing) {
      const search = typeof window !== "undefined" ? window.location.search : "";
      window.location.replace(`${MARKETING_HOME_URL}${search}`);
    }
  }, [redirectToMarketing]);

  if (redirectToMarketing) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-600">
        Redirecionando...
      </div>
    );
  }
  return <MarketingHome />;
};

const NotFound: FC = () => (
  <div className="p-8">404 - Página Não Encontrada</div>
);

// Botão flutuante de WhatsApp
const WhatsAppButton: FC = () => {
  const location = useLocation();

  const shouldHide =
    location.pathname.startsWith("/sala/") ||
    location.pathname.startsWith("/salax/") ||
    location.pathname.startsWith("/sala-beta/") ||
    location.pathname.startsWith("/painel");

  if (shouldHide) return null;

  const whatsappNumber = "5513997701755";
  const message = "Olá! Gostaria de saber mais sobre as constelações.";
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 rounded-full bg-green-500 p-4 text-white shadow-lg transition-all duration-300 hover:scale-110 hover:bg-green-600"
      title="Fale conosco no WhatsApp"
    >
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    </a>
  );
};

const SiteFooter: FC = () => {
  const location = useLocation();

  const shouldHide =
    location.pathname.startsWith("/sala/") ||
    location.pathname.startsWith("/salax/") ||
    location.pathname.startsWith("/sala-beta/");

  if (shouldHide) return null;

  return <Footer />;
};

// Dispara page_view no Google Analytics e Facebook Pixel a cada troca de rota
const Analytics: FC = () => {
  usePageTracking();
  return null;
};

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
  requiresAvatar?: boolean;
}

const ProtectedRoute: FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        Carregando...
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user.role === "login-not-completed") {
    if (location.pathname !== "/completar-perfil") {
      return (
        <Navigate to="/completar-perfil" state={{ from: location }} replace />
      );
    }
  }

  if (allowedRoles && user && user.role && !allowedRoles.includes(user.role)) {
    return <Navigate to="/painel" replace />;
  }

  return <>{children}</>;
};

const App: FC = () => {
  return (
    <div className="min-h-screen bg-gray-100 text-gray-800">
      <DomainRedirect />
      <ClearAnonymousGuestOutsideRoom />
      <Routes>
        {/* Site institucional */}
        <Route path="/" element={<MarketingHomeRoute />} />
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/constelador" element={<ConsteladorPage />} />
        <Route path="/planos" element={<Plans />} />
        <Route path="/contato" element={<Contact />} />
        <Route path="/sobre-nos" element={<SobreNosPage />} />
        <Route path="/tutoriais" element={<TutorialsPage />} />
        <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />
        <Route path="/termos-de-servico" element={<TermsOfService />} />
        <Route path="/terapeutas" element={<Therapists />} />
        <Route path="/quero-participar" element={<OpenRooms />} />
        <Route path="/terapeuta/:id" element={<TherapistShow />} />

        {/* App / plataforma */}
        <Route path="/login" element={<LoginForm />} />
        <Route path="/sair" element={<LogoutRedirect />} />
        <Route path="/esqueci-senha" element={<ForgotPassword />} />
        <Route path="/redefinir-senha" element={<ResetPassword />} />
        <Route path="/__/auth/action" element={<AuthActionHandler />} />
        <Route path="/r/:code" element={<InfluencerRedirect />} />
        <Route path="/sala/:roomId" element={<Meeting />} />
        <Route path="/sala-beta/teste" element={<Navigate to="/sala-beta/sala-livekit-teste" replace />} />
        <Route path="/sala-beta/:roomId" element={<Meeting forceEngine="livekit" />} />
        <Route
          path="/painel"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agendamento"
          element={
            <ProtectedRoute>
              <Schedule />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agendamento/:status"
          element={
            <ProtectedRoute>
              <PaymentStatusPage />
            </ProtectedRoute>
          }
        />
        <Route path="/pagamento/:status" element={<PaymentStatusPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route
          path="/completar-perfil"
          element={
            <ProtectedRoute>
              <CompleteProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salax/:roomId"
          element={
            <ProtectedRoute>
              <Meeting />
            </ProtectedRoute>
          }
        />
        <Route
          path="/painel/configuracoes"
          element={
            <ProtectedRoute allowedRoles={["terapeuta"]}>
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>

      <Analytics />
      <SiteFooter />
      <WhatsAppButton />
      <EnvironmentBadge />
    </div>
  );
};

export default App;
